import { ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";

import AdminContractsCard from "#/components/admin/AdminContractsCard";
import ChangePasswordForm from "#/components/admin/ChangePasswordForm";
import NotificationExamplesCard from "#/components/admin/NotificationExamplesCard";
import SendTestNotificationCard from "#/components/admin/SendTestNotificationCard";
import {
  ArrowPathIcon,
  ArrowRightIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  ShieldExclamationIcon,
} from "@heroicons/react/24/solid";

import BadgeButton from "../BadgeButton";
import { SubmitButton } from "../buttons/submit-button";
import Card from "../card";
import ConfirmationModal from "../ConfirmationModal";
import BooleanIcon from "../details/BooleanIcon";
import DetailItem from "../details/DetailItem";
import DetailSection from "../details/DetailSection";
import EditButton from "../details/EditButton";
import ErrorDisplay from "../ErrorDisplay";
import HarperLoadingSpinner from "../HarperLoadingSpinner";
import InfoRow from "../InfoRow";
import InfoRowLongText from "../InfoRowLongText";
import Input from "../input";
import TamaguiExample from "../tamagui-example";
import ThemedInfoSection from "../ThemedInfoSection";
import ThemedModal from "../ThemedModal";
import Typography from "../typography";
import UnreadBadge from "../UnreadBadge";
import { SharedComponentsCard } from "./SharedComponentsCard";
import ThemeExampleCard from "./ThemeExampleCard";

export interface ThemePageProps {
  themeExample?: ReactNode;
}

export const ThemePageLayout = ({
  themeExample = <ThemeExampleCard />,
}: ThemePageProps) => {
  const navigate = useNavigate();
  const [isModalExampleOpen, setModalExampleOpen] = useState(false);
  const [isThemedModalOpen, setThemedModalOpen] = useState(false);

  const supportSummary = [
    {
      label: "Support Email",
      value: "support@harperhealth.com",
    },
    {
      label: "Priority Escalation",
      value: "Dr. Priya Patel",
    },
    {
      label: "After-hours Line",
      value: "(800) 555-0199",
    },
    {
      label: "Notes",
      value:
        "Escalations route through the triage team before paging the on-call provider.",
    },
  ];

  return (
    <>
      <div className="container mx-auto px-4 py-8">
        <Typography variant="h2" className="mb-8 text-terracotta">
          Theme & Components
        </Typography>

        <div className="mb-10">
          <SubmitButton
            type="button"
            hideArrow
            onClick={() => navigate("/admin")}
          >
            Back to Admin
          </SubmitButton>
        </div>

        <div className="mb-12">
          <ChangePasswordForm />
        </div>

        <SendTestNotificationCard />

        <AdminContractsCard />

        <NotificationExamplesCard />

        <div className="mb-12">{themeExample}</div>

        <SharedComponentsCard
          setModalExampleOpen={setModalExampleOpen}
          setThemedModalOpen={setThemedModalOpen}
        />

        <Card className="mb-12 w-full" variant="default" elevation={1}>
          <Card.Header variant="default">
            <Typography variant="h4" className="text-terracotta">
              Loading & Status Indicators
            </Typography>
          </Card.Header>
          <Card.Body className="space-y-6">
            <HarperLoadingSpinner
              label="Syncing credentials"
              description="We’re verifying the latest EHR access tokens and refreshing cached data."
            />
            <div className="flex flex-wrap items-center gap-4">
              <BooleanIcon value showLabel trueLabel="Active" />
              <BooleanIcon value={false} showLabel falseLabel="Disabled" />
              <BooleanIcon
                value={null}
                showLabel
                neutralLabel="Awaiting review"
                className="bg-cream-100 px-3 py-2 rounded-md"
              />
            </div>
          </Card.Body>
        </Card>

        <div className="mb-12">
          <ThemedInfoSection
            title="Escalation Playbook"
            eyebrow="Operations"
            description="Contextual guidance that admins reference when messages or requests escalate to the on-call clinical lead."
            actions={
              <BadgeButton
                tone="primary"
                variant="solid"
                trailingIcon={
                  <ArrowRightIcon className="h-4 w-4" aria-hidden />
                }
              >
                View SOP
              </BadgeButton>
            }
          >
            <div className="space-y-6">
              <InfoRowLongText
                label="Triage narrative"
                helperText="Summarize how the issue surfaced and what mitigation steps have already occurred."
                text="A member reported adverse reactions during her at-home infusion visit. Nursing staff paused the infusion, provided anti-nausea medication, and monitored vitals for 45 minutes. Symptoms stabilized but the patient still feels uneasy about continuing treatment tonight."
              />
              <InfoRowLongText
                label="Next best step"
                accent="beige"
                text="Connect the on-call provider with the nursing supervisor for a real-time huddle. Confirm whether the infusion should be rescheduled or if additional observation is needed before resuming therapy. Document the clinical decision inside Harper Notes and notify the member once a plan is finalized."
                actions={
                  <BadgeButton tone="neutral" variant="outline">
                    Copy summary
                  </BadgeButton>
                }
              />
            </div>
          </ThemedInfoSection>
        </div>

        <Card className="w-full mb-12" variant="default" elevation={1}>
          <Card.Header variant="default">
            <Typography variant="h4" className="text-terracotta">
              Badge Buttons & Error Banner
            </Typography>
          </Card.Header>
          <Card.Body className="space-y-6">
            <div className="flex flex-wrap gap-3">
              <BadgeButton
                leadingIcon={
                  <CheckCircleIcon className="h-4 w-4" aria-hidden />
                }
              >
                Default state
              </BadgeButton>
              <BadgeButton
                tone="primary"
                variant="solid"
                trailingIcon={
                  <ArrowRightIcon className="h-4 w-4" aria-hidden />
                }
              >
                Solid action
              </BadgeButton>
              <BadgeButton tone="success" isActive>
                Active filter
              </BadgeButton>
              <BadgeButton tone="neutral" variant="outline">
                Neutral outline
              </BadgeButton>
              <BadgeButton tone="danger" variant="soft">
                Needs review
              </BadgeButton>
            </div>

            <ErrorDisplay
              title="Credential sync failed"
              description="We couldn’t refresh the EHR credentials. Retry the sync or reach out to support if the issue persists."
              icon={<ShieldExclamationIcon className="h-5 w-5" aria-hidden />}
              actions={
                <>
                  <BadgeButton
                    tone="primary"
                    variant="solid"
                    leadingIcon={
                      <ArrowPathIcon className="h-4 w-4" aria-hidden />
                    }
                  >
                    Retry sync
                  </BadgeButton>
                  <BadgeButton
                    tone="neutral"
                    variant="outline"
                    trailingIcon={
                      <ArrowRightIcon className="h-4 w-4" aria-hidden />
                    }
                  >
                    Contact support
                  </BadgeButton>
                </>
              }
            />
          </Card.Body>
        </Card>

        <Card className="w-full mb-12" variant="default" elevation={1}>
          <Card.Header variant="default">
            <Typography variant="h4" className="text-terracotta">
              Info Row & Unread Badge Examples
            </Typography>
          </Card.Header>
          <Card.Body>
            <div className="overflow-hidden rounded-lg border border-beige-200 bg-white">
              {supportSummary.map(({ label, value }) => (
                <InfoRow
                  key={label}
                  label={label}
                  value={value}
                  allowWrap
                  className="px-4"
                />
              ))}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-4">
              <Typography variant="subtitle2" className="text-text-dark">
                Notification States
              </Typography>
              <UnreadBadge count={4} />
              <UnreadBadge count={128} maxCount={99} tone="neutral" />
              <UnreadBadge dot tone="success" ariaLabel="No unread alerts" />
            </div>
          </Card.Body>
        </Card>

        <DetailSection
          className="mb-12"
          title="Clinic Profile Snapshot"
          description="Key reference details surfaced for the Harper Care support team."
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <BadgeButton
                tone="primary"
                variant="solid"
                leadingIcon={<ArrowPathIcon className="h-4 w-4" aria-hidden />}
              >
                Refresh profile data
              </BadgeButton>
              <EditButton onClick={() => setThemedModalOpen(true)}>
                Edit details
              </EditButton>
            </div>
          }
        >
          <DetailItem
            icon={<BuildingOffice2Icon className="h-5 w-5" aria-hidden />}
            label="Clinic"
            value="Harper Midtown Care"
            helperText="ID: CLN-204"
          />
          <DetailItem
            icon={<MapPinIcon className="h-5 w-5" aria-hidden />}
            label="Primary Location"
            value="225 5th Avenue, Suite 1800, New York, NY"
            helperText="Three blocks from the Midtown transit hub"
          />
          <DetailItem
            icon={<PhoneIcon className="h-5 w-5" aria-hidden />}
            label="Support Line"
            value="(800) 555-0199"
            helperText="24/7 nurse triage coverage"
          />
          <DetailItem
            icon={<EnvelopeIcon className="h-5 w-5" aria-hidden />}
            label="Escalation Window"
            layout="stack"
            helperText="Outside these hours requests route to the on-call provider."
            value={
              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-terracotta-50 px-3 py-1 text-sm text-terracotta-700">
                  Weekdays 7a – 9p ET
                </span>
                <span className="rounded-full bg-beige-100 px-3 py-1 text-sm text-brown-700">
                  Weekends on-call rotation
                </span>
              </div>
            }
          />
        </DetailSection>

        <Card
          className="w-full mb-12"
          variant="default"
          elevation={1}
          hoverEffect
        >
          <Card.Header variant="default">
            <Typography variant="h4" className="text-terracotta">
              Input Components
            </Typography>
          </Card.Header>
          <Card.Body className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              id="basic-input"
              label="Basic Input"
              placeholder="Type something..."
              fullWidth
            />

            <Input
              id="input-with-helper"
              label="Input with Helper Text"
              helperText="This is a helper text"
              placeholder="Type something..."
              fullWidth
            />

            <Input
              id="input-with-error"
              label="Input with Error"
              error="This field is required"
              placeholder="Type something..."
              fullWidth
            />

            <Input
              id="disabled-input"
              label="Disabled Input"
              placeholder="Type something..."
              disabled
              fullWidth
            />
          </Card.Body>
        </Card>

        <Card className="w-full" variant="default" elevation={1}>
          <Card.Header variant="default">
            <Typography variant="h4" className="text-terracotta">
              Typography Components
            </Typography>
          </Card.Header>
          <Card.Body>
            <div className="space-y-4">
              <Typography variant="h1">Heading 1</Typography>
              <Typography variant="h2">Heading 2</Typography>
              <Typography variant="h3">Heading 3</Typography>
              <Typography variant="h4">Heading 4</Typography>
              <Typography variant="h5">Heading 5</Typography>
              <Typography variant="h6">Heading 6</Typography>
              <Typography variant="subtitle1">Subtitle 1</Typography>
              <Typography variant="subtitle2">Subtitle 2</Typography>
              <Typography variant="body1">
                Body 1: The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography variant="body2">
                Body 2: The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography variant="caption">
                Caption: The quick brown fox jumps over the lazy dog.
              </Typography>
              <Typography variant="button">Button text</Typography>
              <Typography variant="overline">Overline text</Typography>
            </div>
          </Card.Body>
        </Card>

        <div className="mt-12">
          <Typography variant="h2" className="mb-8 text-terracotta">
            Tamagui Styling Example
          </Typography>
          <TamaguiExample />
        </div>
      </div>
      <ConfirmationModal
        isOpen={isModalExampleOpen}
        onClose={() => setModalExampleOpen(false)}
        onConfirm={() => setModalExampleOpen(false)}
        title="Schedule downtime notice"
        message="Confirm that you want to notify all administrators about the planned maintenance window."
        confirmText="Send notice"
        cancelText="Maybe later"
      >
        <Typography variant="caption" className="text-brown-600">
          The modal supports additional guidance below the main copy for
          escalation details or follow-up actions.
        </Typography>
      </ConfirmationModal>
      <ThemedModal
        isOpen={isThemedModalOpen}
        onClose={() => setThemedModalOpen(false)}
        title="Update clinic profile"
        description="Review the clinic narrative and contact details before saving changes to Harper."
        footer={
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-2">
            <EditButton
              tone="neutral"
              onClick={() => setThemedModalOpen(false)}
              ariaLabel="Close modal without saving"
            >
              Cancel
            </EditButton>
            <BadgeButton
              tone="primary"
              variant="solid"
              onClick={() => setThemedModalOpen(false)}
            >
              Save updates
            </BadgeButton>
          </div>
        }
      >
        <div className="space-y-4">
          <Typography variant="body2" className="text-brown-700">
            Keep the support summary and escalation instructions current so the
            care operations team can act quickly when issues surface.
          </Typography>
          <ul className="space-y-2 text-sm text-brown-600">
            <li>• Confirm on-call rotation contacts are accurate.</li>
            <li>• Verify after-hours phone routing procedures.</li>
            <li>• Document any temporary service disruptions.</li>
          </ul>
        </div>
      </ThemedModal>
    </>
  );
};

export default ThemePageLayout;
